import React, { useState, useEffect, useCallback } from 'react';
import { apiUtils } from '../../../utils/apiUtils';
import { useConfirm } from '../../ui';
import SimpleModal from '../../composite/SimpleModal';
import {
  Upload,
  Trash2,
  RefreshCw,
  Play,
  Pause,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Activity,
  TrendingUp,
  Shield,
  Settings,
  Info,
} from 'lucide-react';

const SessionManagement = () => {
  const confirmAction = useConfirm();
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [newSession, setNewSession] = useState({ label: '', cookies: '', scanThreshold: 30 });
  const [behaviorConfig, setBehaviorConfig] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const [sessionsRes, statsRes, configRes] = await Promise.all([
        apiUtils.get('/api/admin/sessions'),
        apiUtils.get('/api/admin/sessions/stats/overview'),
        apiUtils.get('/api/admin/sessions/config/behavior'),
      ]);

      const sessionsData = await sessionsRes.json();
      const statsData = await statsRes.json();
      const configData = await configRes.json();

      if (sessionsData.success) setSessions(sessionsData.data || []);
      if (statsData.success) setStats(statsData.data);
      if (configData.success) setBehaviorConfig(configData.data);
    } catch (err) {
      setError('Failed to load session data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    const interval = setInterval(() => {
      if (!document.hidden) fetchSessions(); // no polling from background tabs
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchSessions]);

  const showNotification = (msg, isError = false) => {
    if (isError) {
      setError(msg);
      setTimeout(() => setError(null), 5000);
    } else {
      setSuccess(msg);
      setTimeout(() => setSuccess(null), 5000);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!newSession.label || !newSession.cookies) {
      showNotification('Label and cookies are required', true);
      return;
    }

    setUploading(true);
    try {
      const response = await apiUtils.post('/api/admin/sessions', {
        label: newSession.label,
        cookies: newSession.cookies,
        scanThreshold: parseInt(newSession.scanThreshold) || 30,
      });

      const data = await response.json();
      if (data.success) {
        showNotification('Session created and sent to scraper for validation');
        setShowUploadModal(false);
        setNewSession({ label: '', cookies: '', scanThreshold: 30 });
        fetchSessions();
      } else {
        showNotification(data.error || 'Failed to create session', true);
      }
    } catch (err) {
      showNotification(err.message || 'Failed to upload session', true);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (sessionId) => {
    if (!(await confirmAction({
      title: `Delete session ${sessionId}?`,
      description: 'Scans that depend on this browser session will fail until a new one is uploaded.',
      confirmLabel: 'Delete session',
      destructive: true,
    }))) return;

    try {
      const response = await apiUtils.delete(`/api/admin/sessions/${sessionId}`);
      const data = await response.json();
      if (data.success) {
        showNotification('Session deleted');
        fetchSessions();
      } else {
        showNotification(data.error, true);
      }
    } catch (err) {
      showNotification('Failed to delete session', true);
    }
  };

  const handleValidate = async (sessionId) => {
    try {
      const response = await apiUtils.post(`/api/admin/sessions/${sessionId}/validate`, {});
      const data = await response.json();
      if (data.success) {
        showNotification('Re-validation triggered');
        fetchSessions();
      } else {
        showNotification(data.error, true);
      }
    } catch (err) {
      showNotification('Failed to trigger validation', true);
    }
  };

  const handleToggle = async (sessionId) => {
    try {
      const response = await apiUtils.post(`/api/admin/sessions/${sessionId}/toggle`, {});
      const data = await response.json();
      if (data.success) {
        showNotification(`Session ${data.data.isActive ? 'enabled' : 'disabled'}`);
        fetchSessions();
      } else {
        showNotification(data.error, true);
      }
    } catch (err) {
      showNotification('Failed to toggle session', true);
    }
  };

  const handleUpdateBehaviorConfig = async () => {
    try {
      const response = await apiUtils.post('/api/admin/sessions/config/behavior', behaviorConfig);
      const data = await response.json();
      if (data.success) {
        showNotification('Behavior config updated and pushed to scraper');
        setShowConfigModal(false);
      } else {
        showNotification(data.error, true);
      }
    } catch (err) {
      showNotification('Failed to update behavior config', true);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      healthy: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300 border-green-200',
      validating: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-300 border-yellow-200',
      burnt: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300 border-red-200',
      rate_limited: 'bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300 border-orange-200',
      expired: 'bg-muted text-foreground border-border',
      threshold_reached: 'bg-purple-100 text-purple-800 dark:bg-purple-500/15 dark:text-purple-300 border-purple-200',
      disabled: 'bg-muted text-muted-foreground border-border',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[status] || styles.burnt}`}>
        {status}
      </span>
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'validating': return <RefreshCw className="w-4 h-4 text-yellow-500 animate-spin" />;
      case 'burnt': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'rate_limited': return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      case 'threshold_reached': return <Pause className="w-4 h-4 text-purple-500" />;
      case 'disabled': return <Pause className="w-4 h-4 text-muted-foreground" />;
      default: return <Activity className="w-4 h-4 text-muted-foreground" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
          <AlertTriangle className="w-5 h-5" />
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2 dark:bg-green-500/10 dark:border-green-500/30 dark:text-green-200">
          <CheckCircle className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <StatCard icon={Shield} label="Total Sessions" value={stats.totalSessions} color="blue" />
          <StatCard icon={CheckCircle} label="Healthy" value={stats.healthySessions} color="green" />
          <StatCard icon={RefreshCw} label="Validating" value={stats.validatingSessions} color="yellow" />
          <StatCard icon={XCircle} label="Burnt" value={stats.burntSessions} color="red" />
          <StatCard icon={AlertTriangle} label="Rate Limited" value={stats.rateLimitedSessions} color="orange" />
          <StatCard icon={TrendingUp} label="Avg Success Rate" value={`${Math.round(stats.avgSuccessRate || 0)}%`} color="purple" />
        </div>
      )}

      {/* Actions Bar */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Scraper Sessions</h2>
          <p className="text-sm text-muted-foreground">Manage X/Twitter scraper sessions and monitor health</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowConfigModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-card border border-input rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            <Settings className="w-4 h-4" />
            Behavior Config
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            <Upload className="w-4 h-4" />
            Add Session
          </button>
        </div>
      </div>

      {/* Sessions Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-muted text-foreground font-medium border-b border-border">
              <tr>
                <th className="px-4 py-3">Session</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Jobs</th>
                <th className="px-4 py-3">Success Rate</th>
                <th className="px-4 py-3">Threshold</th>
                <th className="px-4 py-3">Last Used</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sessions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">
                    No sessions configured. Add a session to start scraping.
                  </td>
                </tr>
              ) : (
                sessions.map((session) => (
                  <tr key={session.sessionId} className="hover:bg-muted">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(session.status)}
                        <div>
                          <div className="font-medium text-foreground">{session.label}</div>
                          <div className="text-xs text-muted-foreground">{session.sessionId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(session.status)}</td>
                    <td className="px-4 py-3">
                      <div className="text-foreground">{session.jobsCompleted}</div>
                      <div className="text-xs text-muted-foreground">{session.jobsFailed} failed</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-accent rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500 rounded-full"
                            style={{ width: `${session.successRate || 0}%` }}
                          />
                        </div>
                        <span className="text-xs">{Math.round(session.successRate || 0)}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-foreground">{session.currentScanCount} / {session.scanThreshold}</span>
                        {session.status === 'threshold_reached' && (
                          <span className="text-xs text-purple-600 font-medium">Limit reached</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {session.lastUsedAt ? formatRelativeTime(new Date(session.lastUsedAt)) : 'Never'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleToggle(session.sessionId)}
                          className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded"
                          title={session.isActive ? 'Disable' : 'Enable'}
                        >
                          {session.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleValidate(session.sessionId)}
                          className="p-1.5 text-muted-foreground hover:text-brand-600 hover:bg-brand-50 rounded dark:bg-brand-500/10"
                          title="Re-validate"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(session.sessionId)}
                          className="p-1.5 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded dark:bg-red-500/10"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Modal */}
      <SimpleModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title="Add New Session"
      >
        <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Session Label</label>
                <input
                  type="text"
                  value={newSession.label}
                  onChange={(e) => setNewSession({ ...newSession, label: e.target.value })}
                  placeholder="e.g., Primary X Account"
                  className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Cookies JSON
                  <span className="text-muted-foreground font-normal ml-1">(exported from browser extension)</span>
                </label>
                <textarea
                  value={newSession.cookies}
                  onChange={(e) => setNewSession({ ...newSession, cookies: e.target.value })}
                  placeholder={`[\n  {\n    "name": "auth_token",\n    "value": "...",\n    "domain": ".x.com"\n  }\n]`}
                  className="w-full h-40 px-3 py-2 border border-input rounded-lg font-mono text-xs focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Paste the full cookies JSON array. You can export this using a browser extension like "EditThisCookie" or "Cookie-Editor".
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Daily Scan Threshold</label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={newSession.scanThreshold}
                  onChange={(e) => setNewSession({ ...newSession, scanThreshold: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum number of scans this session will perform per day. Lower is safer.
                </p>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-foreground hover:bg-muted rounded-lg text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Add Session'}
                </button>
              </div>
            </form>
      </SimpleModal>

      {/* Behavior Config Modal */}
      <SimpleModal
        isOpen={!!(showConfigModal && behaviorConfig)}
        onClose={() => setShowConfigModal(false)}
        title="Behavior Bot Configuration"
        size="xl"
      >
        <div>
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-brand-50 rounded-lg dark:bg-brand-500/10">
                <div className="flex items-center gap-3">
                  <Info className="w-5 h-5 text-brand-600" />
                  <div>
                    <div className="font-medium text-brand-900">Behavior Bot</div>
                    <div className="text-sm text-brand-700">
                      When enabled, the bot periodically performs human-like actions on healthy sessions to keep them warm and avoid detection.
                    </div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={behaviorConfig.enabled}
                    onChange={(e) => setBehaviorConfig({ ...behaviorConfig, enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-accent peer-focus:ring-4 peer-focus:ring-brand-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-input after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-600"></div>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Min Interval (seconds)</label>
                  <input
                    type="number"
                    value={behaviorConfig.globalMinInterval}
                    onChange={(e) => setBehaviorConfig({ ...behaviorConfig, globalMinInterval: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-input rounded-lg"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Minimum time between bot actions</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Max Interval (seconds)</label>
                  <input
                    type="number"
                    value={behaviorConfig.globalMaxInterval}
                    onChange={(e) => setBehaviorConfig({ ...behaviorConfig, globalMaxInterval: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-input rounded-lg"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Maximum time between bot actions</p>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-3">Behavior Actions</h4>
                <div className="space-y-3">
                  {behaviorConfig.behaviors?.map((behavior, index) => (
                    <div key={index} className="p-4 border border-border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium text-foreground">{behavior.action}</div>
                        <div className="text-sm text-muted-foreground">Weight: {behavior.weight}%</div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">{behavior.description}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-muted-foreground">Min Interval</label>
                          <input
                            type="number"
                            value={behavior.minInterval}
                            onChange={(e) => {
                              const newBehaviors = [...behaviorConfig.behaviors];
                              newBehaviors[index].minInterval = parseInt(e.target.value);
                              setBehaviorConfig({ ...behaviorConfig, behaviors: newBehaviors });
                            }}
                            className="w-full px-2 py-1 border border-input rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground">Max Interval</label>
                          <input
                            type="number"
                            value={behavior.maxInterval}
                            onChange={(e) => {
                              const newBehaviors = [...behaviorConfig.behaviors];
                              newBehaviors[index].maxInterval = parseInt(e.target.value);
                              setBehaviorConfig({ ...behaviorConfig, behaviors: newBehaviors });
                            }}
                            className="w-full px-2 py-1 border border-input rounded text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowConfigModal(false)}
                  className="px-4 py-2 text-foreground hover:bg-muted rounded-lg text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateBehaviorConfig}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700"
                >
                  Save & Push to Scraper
                </button>
              </div>
            </div>
        </div>
      </SimpleModal>
    </div>
  );
};

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: 'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-200',
    green: 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-200',
    yellow: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-200',
    red: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-200',
    orange: 'bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200',
    purple: 'bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-200',
  };
  return (
    <div className={`p-4 rounded-lg ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

function formatRelativeTime(date) {
  const seconds = Math.floor((new Date() - date) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default SessionManagement;
